import { DeleteIcon, GripVerticalIcon, PlusIcon, ZapIcon } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { Fragment, useState } from "react";
import { AnchorUnitEnum, PricingAnchor } from "../../pricing-configurator.types";
import { ANCHOR_UNIT_OPTIONS } from "../../pricing-configurator.const";

const ANCHOR_UNIT_VALUES = Object.values(AnchorUnitEnum) as string[];
const isAnchorUnit = (v: string): v is AnchorUnitEnum => ANCHOR_UNIT_VALUES.includes(v);

export type PricingAnchorTableProps = {
  anchors: PricingAnchor[];
  onRemoveAnchor: (index: number) => void;
  onUpdateAnchor: (index: number, field: keyof PricingAnchor, value: number) => void;
  onInsertAnchorAfter: (index: number) => void;
  onReorderAnchors: (fromIndex: number, toIndex: number) => void;
  onToggleShortcut?: (index: number) => void;
  showUnitColumn?: boolean;
  onUpdateAnchorUnit?: (index: number, unit: AnchorUnitEnum) => void;
};

export const PricingAnchorTable = ({
  anchors,
  showUnitColumn = false,
  onRemoveAnchor,
  onUpdateAnchor,
  onInsertAnchorAfter,
  onReorderAnchors,
  onToggleShortcut,
  onUpdateAnchorUnit,
}: PricingAnchorTableProps) => {
  const [editingPriceIndex, setEditingPriceIndex] = useState<string | null>(null);
  const [priceInputValue, setPriceInputValue] = useState("");
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null) return;
    if (dragOverIndex !== index) setDragOverIndex(index);
  };

  const handleDrop = (index: number) => {
    if (draggedIndex !== null && draggedIndex !== index) {
      onReorderAnchors(draggedIndex, index);
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handlePriceBlur = (index: number, value: string) => {
    const parsed = parseFloat(value);
    if (!isNaN(parsed) && parsed >= 0) {
      onUpdateAnchor(index, "priceAmount", parsed);
    }
    setEditingPriceIndex(null);
    setPriceInputValue("");
  };

  const handleDurationChange = (index: number, value: string) => {
    const parsed = parseInt(value);
    if (!isNaN(parsed)) {
      onUpdateAnchor(index, "duration", Math.max(1, parsed));
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-md border overflow-hidden">
        <Table className="table-fixed w-full">
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-9 px-2"></TableHead>
              <TableHead className="px-3">Durée{!showUnitColumn && " (min)"}</TableHead>
              {showUnitColumn && <TableHead className="px-3">Unité</TableHead>}
              <TableHead className="px-3">Prix (€)</TableHead>
              {onToggleShortcut && (
                <TableHead className="w-[88px] text-center px-2">
                  <span className="inline-flex items-center justify-center gap-1 text-xs font-medium text-muted-foreground">
                    Raccourci
                    <ZapIcon className="h-3.5 w-3.5" />
                  </span>
                </TableHead>
              )}
              <TableHead className="w-10 px-2"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {anchors.map((anchor, index) => (
              <Fragment key={anchor.id}>
                <TableRow
                  className={cn(
                    "transition-all",
                    draggedIndex === index && "opacity-50 bg-muted/30",
                    dragOverIndex === index && draggedIndex !== index && "border-t-2 border-t-primary bg-primary/5",
                  )}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDrop={() => handleDrop(index)}
                >
                  <TableCell className="px-2 py-2">
                    <div
                      draggable
                      onDragStart={() => handleDragStart(index)}
                      onDragEnd={handleDragEnd}
                      className="flex cursor-grab items-center justify-center text-muted-foreground hover:text-foreground"
                    >
                      <GripVerticalIcon className="h-4 w-4" />
                    </div>
                  </TableCell>
                  <TableCell className="px-3 py-2">
                    <Input
                      type="number"
                      value={anchor.duration}
                      onChange={(e) => handleDurationChange(index, e.target.value)}
                      className="h-8 w-full"
                      min={1}
                      step={1}
                    />
                  </TableCell>
                  {showUnitColumn && (
                    <TableCell className="px-3 py-2">
                      <Select
                        value={anchor.unit}
                        onValueChange={(v) => {
                          if (isAnchorUnit(v)) onUpdateAnchorUnit?.(index, v);
                        }}
                      >
                        <SelectTrigger className="h-8 w-full">
                          <SelectValue placeholder="Unité" />
                        </SelectTrigger>
                        <SelectContent>
                          {ANCHOR_UNIT_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                  )}
                  <TableCell className="px-3 py-2">
                    <Input
                      type="text"
                      value={editingPriceIndex === anchor.id ? priceInputValue : anchor.priceAmount.toFixed(2)}
                      onFocus={() => {
                        setEditingPriceIndex(anchor.id ?? null);
                        setPriceInputValue(anchor.priceAmount.toFixed(2));
                      }}
                      onChange={(e) => setPriceInputValue(e.target.value)}
                      onBlur={(e) => handlePriceBlur(index, e.target.value)}
                      className="h-8 w-full"
                    />
                  </TableCell>
                  {onToggleShortcut && (
                    <TableCell className="px-2 py-2 text-center">
                      <button
                        type="button"
                        onClick={() => onToggleShortcut(index)}
                        aria-pressed={anchor.isShortcut}
                        title={anchor.isShortcut ? "Retirer des raccourcis" : "Marquer comme raccourci"}
                        className={cn(
                          "inline-flex h-7 w-7 items-center justify-center rounded-md transition-all duration-150 mx-auto",
                          anchor.isShortcut
                            ? "text-amber-500 bg-amber-50 hover:bg-amber-100 ring-1 ring-amber-200 dark:bg-amber-950/40 dark:ring-amber-800 dark:hover:bg-amber-900/50"
                            : "text-muted-foreground hover:text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-950/30",
                        )}
                      >
                        <ZapIcon
                          className={cn(
                            "h-4 w-4 transition-all duration-150",
                            anchor.isShortcut && "fill-amber-400 drop-shadow-sm",
                          )}
                        />
                      </button>
                    </TableCell>
                  )}
                  <TableCell className="px-2 py-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => onRemoveAnchor(index)}
                      disabled={anchors.length === 1}
                      className="h-7 w-7 text-destructive hover:text-destructive hover:bg-destructive/10 disabled:opacity-30 mx-auto flex"
                      title="Supprimer"
                    >
                      <DeleteIcon className="h-3.5 w-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
                {index < anchors.length && (
                  <TableRow key={`insert-${anchor.id}`} className="hover:bg-transparent border-none h-8">
                    <TableCell
                      colSpan={4 + (showUnitColumn ? 1 : 0) + (onToggleShortcut ? 1 : 0)}
                      className="pt-2 text-center"
                    >
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => onInsertAnchorAfter(index)}
                        className="h-7 w-7 opacity-65 text-muted-foreground hover:text-foreground"
                        title={
                          index < anchors.length - 1
                            ? showUnitColumn
                              ? "Insérer un point d'ancrage"
                              : `Insérer entre ${anchor.duration}min et ${anchors[index + 1].duration}min`
                            : "Ajouter un point d'ancrage"
                        }
                      >
                        <span className="flex items-center gap-1">
                          <PlusIcon className="h-4 w-4" />
                          insérer
                        </span>
                      </Button>
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
